/* An agent's chat history: every conversation you may see with it, filtered on the server
   (title, the latest run's status, pinned, the interface it came through) and grouped
   here (by day or by where it came from). Definable's holds only your own. */
import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, GitBranch as GitBranchBase, EllipsisVertical, ListFilter, Plus } from "lucide-react";
import type { LucideProps } from "lucide-react";
import { CloseButton, IconButton, RemovableChip, SearchInput } from "@/shared/components/ui-kit";
import { GhostButton } from "@/shared/components/settings-atoms";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import ChatMenu, { RenameInput, ChatPin } from "./ChatMenu";
import { ViaIcon } from "@/shared/components/ViaIcon";
import { ApiError } from "@/shared/api/errors";
import { toChat, useThreads } from "@/shared/api/threads";
import { catalogFor } from "@/shared/lib/interface-catalog";
import { ageMinutes } from "@/shared/lib/agent-mood";
import { CHAT_DRAFT, chatDraftKey } from "./drafts";
import type { Agent, ApiRunStatus, Chat, Interface, ThreadFilters } from "@/shared/types";
import { menuItem, menuLabel } from "@/shared/components/menuStyles";

/* lucide's props do not carry `title` — see the delivery notes. */
const GitBranch = GitBranchBase as React.ComponentType<LucideProps & { title?: string }>;

const BATCH = 30; // rows rendered per lazy-load step

/* How a thread's latest run stands, told by the colour of its leading icon: going (blue),
   waiting on a person (amber), failed (red). A finished or stopped one stays quiet. */
const STATUS: Partial<Record<ApiRunStatus, { label: string; color: string }>> = {
  queued: { label: "Running", color: "text-[var(--info)] motion-safe:animate-pulse" },
  running: { label: "Running", color: "text-[var(--info)] motion-safe:animate-pulse" },
  pending: { label: "Waiting on you", color: "text-[var(--warn)]" },
  error: { label: "Failed", color: "text-[var(--danger)]" },
};

function ChatRow({ agent, chat, active, onOpen, onRename, onTogglePin, onBranch, onArchive, onDelete }: {
  agent: Agent;
  chat: Chat;
  active?: boolean;
  onOpen: () => void;
  onRename: (title: string) => void;
  onTogglePin: () => void;
  onBranch: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const status = chat.lastRunStatus && STATUS[chat.lastRunStatus];
  if (renaming) {
    return (
      <div className="flex h-11 lg:h-9 items-center px-1">
        <RenameInput
          value={chat.title}
          onSubmit={(t) => { onRename(t); setRenaming(false); }}
          onCancel={() => setRenaming(false)}
          className="w-full h-7 px-2 rounded-[8px] text-[16px] lg:text-[13px] bg-[var(--surface)] text-[var(--text-primary)] ring-1 ring-[var(--hairline-focus)] outline-none"
        />
      </div>
    );
  }
  return (
    <div className={`group flex items-center rounded-[8px] ${active ? "bg-[var(--accent-active)]" : "hover:bg-[var(--accent-hover)]"}`}>
      <button type="button" onClick={onOpen} aria-current={active ? "true" : undefined}
        className={`press h-11 lg:h-9 min-w-0 flex-1 flex items-center gap-2 pl-2 pr-2 text-left rounded-[8px] ${active ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}>
        {/* The origin icon, in its tile, wears the status colour; `!text-current` lets the channel glyph take it. */}
        <span title={status?.label} className={`w-[22px] h-[22px] shrink-0 rounded-[7px] flex items-center justify-center ${active ? "bg-[var(--surface)] ring-1 ring-[var(--hairline)]" : "bg-[var(--surface-2)]"} ${status?.color ?? (active ? "text-[var(--text-primary)]" : "text-[var(--text-tertiary)]")}`}>
          {chat.via ? <ViaIcon type={chat.via} size={13} className="!text-current" /> : <MessageSquare className="w-[13px] h-[13px]" strokeWidth={1.75} />}
          {status && <span className="sr-only">{status.label}</span>}
        </span>
        <span title={chat.title} className={`min-w-0 flex-1 truncate text-[13px] ${active ? "font-medium" : ""}`}>{chat.title}</span>
        {chat.pinned && <ChatPin size={12} className="shrink-0 text-[var(--text-tertiary)]" />}
        {chat.branchedFrom && <GitBranch className="w-3 h-3 shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} title={`Branched from ${chat.branchedFrom.title}`} />}
        <span className="shrink-0 text-[11px] text-[var(--text-quaternary)] font-mono tabular-nums lg:group-hover:hidden lg:group-has-[:focus-visible]:hidden lg:group-has-[[data-state=open]]:hidden">{chat.updatedAt || "Now"}</span>
      </button>
      <ChatMenu
        agent={agent}
        chat={chat}
        align="end"
        onRename={() => setRenaming(true)}
        onTogglePin={onTogglePin}
        onBranch={onBranch}
        onArchive={onArchive}
        onDelete={onDelete}
        trigger={
          <button
            onClick={(e) => e.stopPropagation()}
            title="Chat options"
            aria-label={`Options for ${chat.title}`}
            className="press shrink-0 w-7 h-7 mr-1 rounded-[8px] flex lg:hidden lg:group-hover:flex lg:group-has-[:focus-visible]:flex lg:data-[state=open]:flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
          >
            <EllipsisVertical className="w-3.5 h-3.5" strokeWidth={1.75} />
          </button>
        }
      />
    </div>
  );
}

type Status = "all" | NonNullable<ThreadFilters["status"]>;
type Grouping = "day" | "interface";
const SEARCH_WAIT_MS = 250;

/* The filter menu's status choices; their dots are the legend for the row colours. */
const SHOW: { value: Status; label: string; dot?: string }[] = [
  { value: "all", label: "All chats" },
  { value: "running", label: "Running", dot: "bg-[var(--info)]" },
  { value: "waiting", label: "Waiting on you", dot: "bg-[var(--warn)]" },
  { value: "failed", label: "Failed", dot: "bg-[var(--danger)]" },
];

/** Which day-group a chat falls in, from when it was last active. */
function dayOf(chat: Chat, now = new Date()): string {
  const minutes = chat.activeAt ? (now.getTime() - new Date(chat.activeAt).getTime()) / 60_000 : ageMinutes(chat.updatedAt);
  const startOfToday = now.getHours() * 60 + now.getMinutes();
  if (minutes <= startOfToday) return "Today";
  if (minutes <= startOfToday + 24 * 60) return "Yesterday";
  if (minutes <= startOfToday + 7 * 24 * 60) return "This week";
  return "Earlier";
}

/** The chats in groups, in order: pinned ones first when grouping by day. */
function grouped(chats: Chat[], by: Grouping): [string, Chat[]][] {
  const groups = new Map<string, Chat[]>();
  for (const chat of chats) {
    const key = by === "interface"
      ? chat.via ? catalogFor(chat.via)?.name ?? chat.via : "In the app"
      : chat.pinned ? "Pinned" : dayOf(chat);
    groups.set(key, [...(groups.get(key) ?? []), chat]);
  }
  return [...groups];
}

export default function ChatHistory({
  agent, from = null, onShowAll, activeChatId, onSelectChat, onNewChat,
  onRenameChat, onTogglePinChat, onBranchChat, onArchiveChat, onDeleteChat, onClose,
}: {
  agent?: Agent | null;
  onClose?: () => void;
  /** "View chats" on an interface: only the conversations that came through it. */
  from?: Interface | null;
  /** Leaves `from` for every chat again. */
  onShowAll?: () => void;
  activeChatId?: string | null;
  onSelectChat: (agentId: string, chatId: string) => void;
  /** A new, empty conversation in the Chat tab. */
  onNewChat?: (agentId: string) => void;
  onRenameChat: (agentId: string, chatId: string, title: string) => void;
  onTogglePinChat: (agentId: string, chatId: string) => void;
  onBranchChat: (agentId: string, chatId: string) => void;
  onArchiveChat: (agentId: string, chatId: string) => void;
  onDeleteChat: (agentId: string, chatId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [asked, setAsked] = useState("");
  const [status, setStatus] = useState<Status>("all");
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const [by, setBy] = useState<Grouping>("day");
  const [limit, setLimit] = useState(BATCH);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // The search goes to the server once typing pauses.
  useEffect(() => {
    const wait = setTimeout(() => setAsked(query.trim()), SEARCH_WAIT_MS);
    return () => clearTimeout(wait);
  }, [query]);

  const filters: ThreadFilters = {
    interfaceId: from?.id, q: asked || undefined, status: status === "all" ? undefined : status,
    pinned: pinnedOnly || undefined,
  };
  const listed = useThreads(agent?.id ?? null, filters);
  const loading = !!agent && listed.isPending;
  const chats = (listed.data ?? []).map(toChat);
  const visible = chats.slice(0, limit);
  const hasMore = chats.length > limit;
  const narrowed = status !== "all" || pinnedOnly;
  const filtering = !!asked || narrowed;
  const clearFilters = () => { setQuery(""); setStatus("all"); setPinnedOnly(false); };

  // Lazy load: grow the window whenever the sentinel scrolls into view.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setLimit((l) => l + BATCH); },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore]);

  // A new search or filter resets the window.
  useEffect(() => { setLimit(BATCH); }, [asked, status, pinnedOnly, from?.id]);

  if (!agent) return null;

  // "@acme_support_bot on Telegram"; a channel alone when it has no name yet.
  const channel = from && (catalogFor(from.type)?.name ?? from.type);
  const where = from && (from.label ? `${from.label} on ${channel}` : channel);
  const count = `${chats.length} chat${chats.length === 1 ? "" : "s"}${channel ? ` via ${channel}` : ""}`;
  const privacy = agent.definable ? "Only you can see your chats with Builder." : undefined;

  /* One group, or one state, as a white card in a grey tray. */
  const tray = "rounded-[18px] bg-[var(--surface-2)] p-1";
  const card = "rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift";

  return (
    <section aria-label="Chat history" className="flex h-full min-h-0 min-w-0 flex-col bg-[var(--surface-sunk)]">
      {/* Lines up with the chat's 56px header row in the card beside it. */}
      <header className="h-14 shrink-0 flex items-center gap-1 pl-4 pr-3">
        <h2 title={privacy} className="m-0 text-[14px] font-semibold tracking-[-0.01em]">Chats</h2>
        <span aria-hidden="true" className="flex-1 pl-1 text-[12.5px] text-[var(--text-quaternary)] tabular-nums">{loading || listed.isError ? "" : chats.length}</span>
        {/* a new chat starts empty: what an earlier new chat left unsent goes */}
        {onNewChat && <IconButton title="New chat" aria-label="New chat" onClick={() => { CHAT_DRAFT.discard(chatDraftKey(agent.id, null)); onNewChat(agent.id); }}><Plus className="w-[14px] h-[14px]" strokeWidth={1.75} /></IconButton>}
        {onClose && <CloseButton onClick={onClose} aria-label="Close chats" title="Close chats" />}
      </header>
      <div className="shrink-0 flex items-center gap-1.5 px-3">
        <SearchInput raised value={query} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)} placeholder="Search chats" aria-label="Search chats" className="flex-1 min-w-0 [&_input]:text-[16px] lg:[&_input]:text-[13px]" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton size="md" active={narrowed} title="Filter and group" aria-label="Filter and group chats" className="relative">
              <ListFilter className="w-[14px] h-[14px]" strokeWidth={1.75} />
              {narrowed && <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[var(--info)]" />}
            </IconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[200px] p-1 rounded-[12px] bg-[var(--surface)] border border-[var(--hairline-strong)] shadow-menu">
            <DropdownMenuRadioGroup value={status} onValueChange={(v) => setStatus(v as Status)}>
              <DropdownMenuLabel className={menuLabel}>Show</DropdownMenuLabel>
              {SHOW.map((s) => (
                <DropdownMenuRadioItem key={s.value} value={s.value} className={menuItem}>
                  <span className="flex-1">{s.label}</span>
                  {s.dot && <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuCheckboxItem checked={pinnedOnly} onCheckedChange={setPinnedOnly} className={menuItem}>Pinned only</DropdownMenuCheckboxItem>
            <DropdownMenuSeparator className="-mx-1 my-1 h-px bg-[var(--hairline)]" />
            <DropdownMenuRadioGroup value={by} onValueChange={(v) => setBy(v as Grouping)}>
              <DropdownMenuLabel className={menuLabel}>Group by</DropdownMenuLabel>
              <DropdownMenuRadioItem value="day" className={menuItem}>Day</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="interface" className={menuItem}>Channel</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {from && (
        <div className="shrink-0 px-3 pt-2">
          <RemovableChip removeLabel="Show all chats" onRemove={() => onShowAll?.()}>
            <ViaIcon type={from.type} size={12} decorative />
            <span className="truncate" title={`Chats that came in through ${where}`}>{where}</span>
          </RemovableChip>
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-3 pt-3 pb-3">
        <p className="sr-only" aria-live="polite">{loading ? "Loading…" : listed.isError ? "History unavailable" : count}</p>
        {listed.isError ? (
          <div className={tray}>
            <div role="alert" className={`${card} p-3 text-[12.5px] text-[var(--text-secondary)]`}>
              <p className="m-0">{listed.error.message}</p>
              {listed.error instanceof ApiError && listed.error.request_id && <p className="m-0 mt-1 font-mono tabular-nums text-[11px] text-[var(--text-tertiary)]">ref {listed.error.request_id}</p>}
              <GhostButton className="mt-2.5" onClick={() => listed.refetch()}>Try again</GhostButton>
            </div>
          </div>
        ) : loading ? (
          <div className={tray}>
            <p className={`${card} m-0 px-3 py-4 text-[12.5px] text-[var(--text-quaternary)]`}>Loading chats…</p>
          </div>
        ) : chats.length === 0 ? (
          <div className={tray}>
            <div className={`${card} flex flex-col items-center gap-3 px-5 py-10 text-center fade-in`}>
              <div className="w-10 h-10 rounded-[12px] bg-[var(--surface-2)] flex items-center justify-center">
                <MessageSquare className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-[14px] font-medium">{filtering ? "No matches" : "No chats yet"}</div>
                <div className="text-[12.5px] text-[var(--text-tertiary)] mt-1 max-w-[240px]">
                  {filtering
                    ? "No chat matches these filters."
                    : from
                      ? `When someone writes to ${where}, the conversation shows up here.`
                      : `Start a chat and it will appear here.${privacy ? ` ${privacy}` : ""}`}
                </div>
              </div>
              {filtering && <GhostButton onClick={clearFilters}>Clear filters</GhostButton>}
            </div>
          </div>
        ) : (
          <div className="space-y-2.5">
            {grouped(visible, by).map(([label, group]) => (
              <section key={label} aria-label={label} className={tray}>
                <div className="flex items-center justify-between px-2.5 pt-1 pb-1.5 text-[12px] text-[var(--text-tertiary)]">
                  <span>{label}</span>
                  <span className="tabular-nums">{group.length}</span>
                </div>
                <div className={`${card} p-1`}>
                  {group.map((c) => (
                    <ChatRow
                      key={c.id}
                      agent={agent}
                      chat={c}
                      active={c.id === activeChatId}
                      onOpen={() => onSelectChat(agent.id, c.id)}
                      onRename={(t) => onRenameChat(agent.id, c.id, t)}
                      onTogglePin={() => onTogglePinChat(agent.id, c.id)}
                      onBranch={() => onBranchChat(agent.id, c.id)}
                      onArchive={() => onArchiveChat(agent.id, c.id)}
                      onDelete={() => onDeleteChat(agent.id, c.id)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {hasMore && (
          <div ref={sentinelRef} className="py-4 text-center text-[12px] text-[var(--text-quaternary)]">
            Loading more…
          </div>
        )}
      </div>
    </section>
  );
}
